/**
 * Blinkit-theme product tile — used by the home rails, category grids, search results, shop menus and "similar
 * products" on the product page, so a product looks the same wherever it appears.
 *
 * Shows (when the data exists): image, % OFF badge, name, pack size / option count, shop, selling price, struck MRP,
 * low-stock / sold-out state, and the shared InlineCartControl (ADD, then − qty +). Prices are exactly what the
 * server priced; the only arithmetic here is the display percentage between two server numbers.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, LayoutGrid } from "lucide-react";
import InlineCartControl, { LowStockHint } from "../InlineCartControl";
import BookingModal from "../BookingModal";

export const bkInr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/** Price / pack line the tile shows: the default (else first) enabled variant when the product has options. */
export function tileDisplay(p) {
    const variants = (p?.variants || []).filter((v) => v && v.enabled !== false);
    if (variants.length) {
        const v = variants.find((x) => x.is_default) || variants[0];
        return {
            sp: v.selling_price ?? p.selling_price,
            mrp: v.mrp ?? p.mrp,
            unit: v.unit_of_measure || v.label || p.unit_of_measure || null,
            options: variants.length,
        };
    }
    return { sp: p?.selling_price, mrp: p?.mrp, unit: p?.unit_of_measure || null, options: 0 };
}

export function discountPct(sp, mrp) {
    const s = Number(sp), m = Number(mrp);
    if (!s || !m || m <= s) return 0;
    return Math.round(((m - s) / m) * 100);
}

export function isSoldOut(p) {
    return p?.track_stock !== false && (p?.status === "OUT_OF_STOCK" || (p?.stock != null && Number(p.stock) <= 0));
}

export default function BkProductTile({ product: p, qty = 0, layout = "grid", showShop = true, testidPrefix = "bk-tile" }) {
    const [booking, setBooking] = useState(false);
    const { sp, mrp, unit, options } = tileDisplay(p);
    const off = discountPct(sp, mrp);
    const img = (p.images || [])[0] || p.image_url || null;
    const soldOut = isSoldOut(p);
    const shop = p.shop_name || p.restaurant_name;
    const width = layout === "rail" ? "shrink-0 w-[148px] sm:w-[160px] lg:w-[176px]" : "w-full min-w-0";

    return (
        <div className={`${width} bg-card rounded-2xl border border-border/80 p-2 flex flex-col`}
             data-testid={`${testidPrefix}-${p.id}`} data-shop-id={p.restaurant_id}>
            <Link to={`/product/${p.id}`} className="block" data-testid={`${testidPrefix}-link-${p.id}`} aria-label={p.name}>
                <div className="relative aspect-square rounded-xl bg-muted/50 overflow-hidden flex items-center justify-center">
                    {img ? (
                        <img src={img} alt="" loading="lazy" className={`w-full h-full object-contain p-1.5 ${soldOut ? "opacity-50 grayscale" : ""}`} />
                    ) : (
                        <LayoutGrid size={24} className="text-muted-foreground/40" />
                    )}
                    {off > 0 && !soldOut && (
                        <span className="absolute top-1.5 left-1.5 rounded-md bg-primary text-primary-foreground text-[10px] font-extrabold px-1.5 py-0.5" data-testid={`${testidPrefix}-off-${p.id}`}>
                            {off}% OFF
                        </span>
                    )}
                    {soldOut && (
                        <span className="absolute inset-x-2 bottom-2 rounded-md bg-foreground/85 text-background text-[10px] font-bold uppercase tracking-wider text-center py-1">
                            Sold out
                        </span>
                    )}
                </div>
                <p className="mt-2 text-[13px] font-semibold leading-snug line-clamp-2 min-h-[2.6em]">{p.name}</p>
            </Link>
            <p className="text-[11.5px] text-muted-foreground leading-tight truncate min-h-[1.25em]">
                {options >= 2 ? `${options} options` : (unit || "")}
            </p>
            {showShop && shop && (
                <Link to={`/restaurant/${p.restaurant_id}`} className="text-[11px] text-muted-foreground/90 truncate hover:text-primary leading-tight mt-0.5">
                    {shop}
                </Link>
            )}
            <div className="min-h-[14px] mt-0.5"><LowStockHint product={p} /></div>
            <div className="mt-auto pt-1.5 flex flex-wrap items-end justify-between gap-x-1.5 gap-y-1.5">
                <div className="min-w-0 leading-tight">
                    <p className="text-[14px] font-extrabold tabular-nums">{bkInr(sp)}</p>
                    {off > 0 && <p className="text-[11px] text-muted-foreground line-through tabular-nums">{bkInr(mrp)}</p>}
                </div>
                {p.kind === "SERVICE" ? (
                    <button type="button" onClick={() => setBooking(true)} data-testid={`${testidPrefix}-book-${p.id}`}
                            className="ml-auto h-9 min-w-[68px] rounded-lg border-2 border-primary text-primary text-xs font-bold tracking-wider inline-flex items-center justify-center gap-1 active:scale-95 transition">
                        <CalendarClock size={12} /> BOOK
                    </button>
                ) : (
                    <div className="ml-auto"><InlineCartControl product={p} currentQty={qty} size="sm" compact /></div>
                )}
            </div>
            {booking && <BookingModal service={p} onClose={() => setBooking(false)} />}
        </div>
    );
}
